import React, { useState, useEffect, useRef } from "react";
import {
  MOODS,
  getEntry,
  saveEntry,
  deleteEntry,
  formatDateHuman,
  isToday,
  type DiaryEntry,
} from "../api/mockData";

interface EntryEditorProps {
  dateStr: string;
  onClose: () => void;
  onSave: () => void;
}

export function EntryEditor({ dateStr, onClose, onSave }: EntryEditorProps) {
  const existing = getEntry(dateStr);
  const [mood, setMood] = useState<1 | 2 | 3 | 4 | 5>(existing?.mood || 4);
  const [text, setText] = useState(existing?.text || "");
  const [showDelete, setShowDelete] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    // Focus textarea with slight delay for animation
    const t = setTimeout(() => textareaRef.current?.focus(), 400);
    return () => clearTimeout(t);
  }, []);

  const handleSave = () => {
    if (!text.trim() && !existing) return;

    const now = new Date().toISOString();
    const entry: DiaryEntry = {
      date: dateStr,
      mood,
      text: text.trim(),
      createdAt: existing?.createdAt || now,
      updatedAt: now,
    };
    saveEntry(entry);
    onSave();
    onClose();
  };

  const handleDelete = () => {
    deleteEntry(dateStr);
    onSave();
    onClose();
  };

  const selectedMood = MOODS.find((m) => m.value === mood);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 backdrop-blur-overlay"
        style={{ background: "oklch(0.22 0.02 50 / 0.3)" }}
      />

      {/* Editor panel */}
      <div
        className="relative w-full max-w-[430px] rounded-t-3xl animate-slide-up"
        style={{
          background: "var(--card)",
          maxHeight: "92dvh",
          boxShadow: "0 -8px 40px oklch(0 0 0 / 0.08)",
        }}
      >
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div
            className="w-10 h-1 rounded-full"
            style={{ background: "var(--border)" }}
          />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-2">
          <button
            onClick={onClose}
            className="text-sm font-medium px-3 py-1.5 rounded-lg transition-colors hover:bg-secondary"
            style={{ color: "var(--muted-foreground)" }}
          >
            Отмена
          </button>
          <div className="text-center">
            <div className="text-sm font-medium" style={{ color: "var(--foreground)" }}>
              {isToday(dateStr) ? "Сегодня" : formatDateHuman(dateStr)}
            </div>
          </div>
          <button
            onClick={handleSave}
            className="text-sm font-semibold px-3 py-1.5 rounded-lg transition-all"
            style={{
              color: text.trim() || existing ? "var(--primary)" : "var(--muted-foreground)",
              opacity: text.trim() || existing ? 1 : 0.5,
            }}
          >
            Сохранить
          </button>
        </div>

        {/* Mood selector */}
        <div className="px-5 py-4">
          <p
            className="text-xs font-medium uppercase tracking-widest mb-3"
            style={{ color: "var(--muted-foreground)" }}
          >
            Как настроение?
          </p>
          <div className="flex items-center justify-between gap-2">
            {MOODS.map((m) => {
              const isSelected = mood === m.value;
              return (
                <button
                  key={m.value}
                  onClick={() => setMood(m.value)}
                  className="flex flex-col items-center gap-1.5 flex-1 py-3 rounded-2xl transition-all duration-200"
                  style={{
                    background: isSelected ? m.softColor : "transparent",
                    transform: isSelected ? "scale(1.08)" : "scale(1)",
                    border: isSelected ? `2px solid ${m.color}` : "2px solid transparent",
                  }}
                >
                  <span
                    className="text-2xl transition-transform duration-200"
                    style={{
                      transform: isSelected ? "scale(1.2)" : "scale(1)",
                      filter: isSelected ? "none" : "grayscale(0.4)",
                    }}
                  >
                    {m.emoji}
                  </span>
                  <span
                    className="text-[10px] font-medium"
                    style={{
                      color: isSelected ? m.color : "var(--muted-foreground)",
                      opacity: isSelected ? 1 : 0.7,
                    }}
                  >
                    {m.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Divider */}
        <div className="mx-5" style={{ height: "1px", background: "var(--border)" }} />

        {/* Text area */}
        <div className="px-5 py-4">
          <textarea
            ref={textareaRef}
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder="Расскажите о своём дне..."
            className="diary-input w-full bg-transparent border-none outline-none min-h-[180px]"
            style={{
              color: "var(--foreground)",
            }}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 pb-[max(1.5rem,env(safe-area-inset-bottom))]">
          <div className="flex items-center gap-2">
            <span className="text-lg">{selectedMood?.emoji}</span>
            <span className="text-xs" style={{ color: "var(--muted-foreground)" }}>
              {text.length > 0 && `${text.length} символов`}
            </span>
          </div>
          {existing && (
            <div>
              {showDelete ? (
                <div className="flex items-center gap-2 animate-scale-in">
                  <button
                    onClick={handleDelete}
                    className="text-xs font-medium px-3 py-1.5 rounded-lg"
                    style={{ background: "oklch(0.577 0.245 27.325 / 0.1)", color: "var(--destructive)" }}
                  >
                    Удалить
                  </button>
                  <button
                    onClick={() => setShowDelete(false)}
                    className="text-xs px-3 py-1.5 rounded-lg"
                    style={{ color: "var(--muted-foreground)" }}
                  >
                    Отмена
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => setShowDelete(true)}
                  className="p-2 rounded-lg transition-colors hover:bg-secondary"
                  style={{ color: "var(--muted-foreground)" }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="3 6 5 6 21 6" />
                    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
